import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Brain, Sigma, BookOpenText, Boxes, ListOrdered, ScanEye, Lightbulb, Compass, ArrowRight, CheckCircle2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const SUBTEST_ICONS = {
  numerical_reasoning: Sigma, vocabulary: BookOpenText, inductive_reasoning: Boxes,
  spatial_visualization: Compass, sequential_reasoning: ListOrdered, visual_comparison_speed: ScanEye,
  idea_generation: Lightbulb, work_approach: Brain,
};

const SUBTESTS_DISPLAY = [
  { key: "numerical_reasoning", name: "Numerical Reasoning" },
  { key: "vocabulary", name: "Vocabulary" },
  { key: "inductive_reasoning", name: "Inductive Reasoning" },
  { key: "spatial_visualization", name: "Spatial Visualization" },
  { key: "sequential_reasoning", name: "Sequential Reasoning" },
  { key: "visual_comparison_speed", name: "Visual Comparison Speed" },
  { key: "idea_generation", name: "Idea Generation" },
  { key: "work_approach", name: "Work Approach Style" },
];

export default function AptitudeIntro() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    api.get("/student/aptitude/status").then((res) => setStatus(res.data)).catch(() => setStatus({})).finally(() => setLoading(false));
  }, []);

  const handleStart = async () => {
    setStarting(true);
    try {
      await api.post("/student/aptitude/start");
      navigate("/aptitude/test");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the Aptitude Test");
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="aptitude-intro-loading">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  const isDone = status?.report_status === "READY";
  const isInProgress = status?.session_status === "in_progress";

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="aptitude-intro-page">
      <div className="max-w-3xl mx-auto">
        <BrandLogo className="h-6 mb-8" />
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-indigo-600 mb-3">Supplementary Module</p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-3" data-testid="aptitude-intro-heading">The Aptitude Test</h1>
        <p className="text-base text-slate-600 leading-relaxed mb-8 max-w-2xl">
          8 short, performance-based exercises — inspired by aptitude batteries like YouScience — that reinforce
          your main CareerStudy360 report with objective reasoning, speed and ideation data. Roughly 15-20 minutes.
        </p>

        <div className="grid sm:grid-cols-2 gap-3 mb-10">
          {SUBTESTS_DISPLAY.map((s) => {
            const Icon = SUBTEST_ICONS[s.key];
            return (
              <div key={s.key} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`aptitude-subtest-chip-${s.key}`}>
                <span className="h-9 w-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-indigo-600" />
                </span>
                <span className="text-sm font-medium text-slate-800">{s.name}</span>
              </div>
            );
          })}
        </div>

        {isDone ? (
          <Button data-testid="aptitude-view-report-button" onClick={() => navigate("/aptitude/report")} className="rounded-full px-6">
            <CheckCircle2 className="h-4 w-4 mr-2" /> View Your Aptitude Test Report
          </Button>
        ) : (
          <Button data-testid="aptitude-start-button" disabled={starting} onClick={handleStart} className="rounded-full px-6">
            {starting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isInProgress ? "Resume Aptitude Test" : "Start Aptitude Test"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
